import React, { useEffect, useState, useRef, useMemo } from "react";
import styles from "./TenantInvite.module.css";
import { useSelector } from "react-redux";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const AVAILABLE_PERMISSIONS = ["read", "delete", "manage"];
const ALL_PERMISSIONS = AVAILABLE_PERMISSIONS;

const CREATE_PERMISSIONS = [
  {
    id: "create_apps",
    label: "Create Apps",
    description: "Allows creating new applications"
  },
];

const AVAILABLE_ROLES = ["MANAGER", "DEVELOPER", "CUSTOMER_CARE_EXECUTIVE"];

const DEPARTMENT_FIELD_CANDIDATES = ["department", "dept", "team", "group"];
const NAME_FIELD_CANDIDATES = ["full_name", "name", "display_name"];

const MAX_INTAKE_QUESTIONS = 10;
const INTAKE_QUESTION_TYPES = ["text", "textarea", "number"];
const MAX_LABEL_LENGTH = 200;

function generateQuestionId() {
  return `q_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 7)}`;
}

/**
 * Normalizes a raw support-user object into a consistent shape.
 *
 * NOTE: `department` here is a DERIVED display key used only by the
 * hierarchy tree (grouping users). It falls back to the first
 * `user_roles` entry when no explicit `department`/`dept`/`team`/`group`
 * field is present. It is NOT the same thing as the per-app
 * `departments` map (which is admin-managed and used for routing).
 */
function normalizeUser(rawUser) {
  const departmentField = DEPARTMENT_FIELD_CANDIDATES.find(
    (f) => rawUser?.[f] != null && String(rawUser[f]).trim() !== ""
  );
  const nameField = NAME_FIELD_CANDIDATES.find(
    (f) => rawUser?.[f] != null && String(rawUser[f]).trim() !== ""
  );

  const email = rawUser?.email || rawUser?.user_email || "";
  const rolesArr = Array.isArray(rawUser?.user_roles) ? rawUser.user_roles : [];

  const department =
    (departmentField && String(rawUser[departmentField])) ||
    (rolesArr.length > 0 ? String(rolesArr[0]) : null) ||
    "Unassigned";

  return {
    ...rawUser,
    email,
    name:
      (nameField && rawUser[nameField]) ||
      (email.split("@")[0] || "Unknown"),
    department,
    roles: rolesArr,
  };
}

function groupUsersByDepartment(users) {
  const groups = users.reduce((acc, user) => {
    const dept = user.department || "Unassigned";
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(user);
    return acc;
  }, {});

  return Object.keys(groups)
    .sort((a, b) => a.localeCompare(b))
    .reduce((acc, dept) => {
      acc[dept] = groups[dept].sort((a, b) =>
        String(a.name).localeCompare(String(b.name))
      );
      return acc;
    }, {});
}

/**
 * Popover editor for a department's intake questions.
 *
 * Renders a small modal-like popover anchored over the parent cell.
 * The admin can add up to MAX_INTAKE_QUESTIONS questions; each is
 * editable (label, type, required) and removable.
 *
 * Fully controlled: `questions` is an array of
 *   { id, label, type, required }
 * `onSave(next)` fires with the trimmed list on Save.
 */
function IntakeQuestionsPopover({
  appName,
  departmentName,
  questions,
  onSave,
  onClose,
}) {
  const [drafts, setDrafts] = useState(() =>
    (Array.isArray(questions) ? questions : []).map((q) => ({ ...q }))
  );
  const [error, setError] = useState("");

  const atCap = drafts.length >= MAX_INTAKE_QUESTIONS;

  const addQuestion = () => {
    if (atCap) return;
    setDrafts((prev) => [
      ...prev,
      {
        id: generateQuestionId(),
        label: "",
        type: "text",
        required: false,
      },
    ]);
  };

  const updateField = (id, patch) => {
    setDrafts((prev) =>
      prev.map((q) => (q.id === id ? { ...q, ...patch } : q))
    );
  };

  const removeQuestion = (id) => {
    setDrafts((prev) => prev.filter((q) => q.id !== id));
  };

  const handleSave = () => {
    const cleaned = [];
    const seenIds = new Set();

    for (const q of drafts) {
      const label = String(q.label || "").trim();
      if (!label) {
        setError("Every question needs a label.");
        return;
      }
      if (label.length > MAX_LABEL_LENGTH) {
        setError(`Labels must be ${MAX_LABEL_LENGTH} characters or fewer.`);
        return;
      }
      if (seenIds.has(q.id)) continue;
      seenIds.add(q.id);
      cleaned.push({
        id: q.id,
        label,
        type: INTAKE_QUESTION_TYPES.includes(q.type) ? q.type : "text",
        required: Boolean(q.required),
      });
    }

    setError("");
    onSave(cleaned);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
          width: 520,
          maxWidth: "92vw",
          maxHeight: "82vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
              Intake questions
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {appName} · <strong>{departmentName}</strong>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 20,
              lineHeight: 1,
              color: "#6b7280",
              padding: 4,
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: "16px 20px",
            overflowY: "auto",
            flex: 1,
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              marginBottom: 12,
              lineHeight: 1.5,
            }}
          >
            These questions will be shown to the visitor right after they
            pick this department. Their answers will appear as the first
            message in the chat for whoever accepts.
          </div>

          {drafts.length === 0 && (
            <div
              style={{
                padding: "20px 12px",
                border: "1px dashed #d1d5db",
                borderRadius: 8,
                textAlign: "center",
                color: "#9ca3af",
                fontSize: 12,
              }}
            >
              No questions yet — this department will connect the visitor
              directly.
            </div>
          )}

          {drafts.map((q, idx) => (
            <div
              key={q.id}
              style={{
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                marginBottom: 8,
                background: "#f9fafb",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <span style={{ fontSize: 11, color: "#6b7280" }}>
                  Question {idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeQuestion(q.id)}
                  title="Remove question"
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#ef4444",
                    cursor: "pointer",
                    fontSize: 12,
                    padding: "2px 6px",
                  }}
                >
                  Remove
                </button>
              </div>

              <input
                type="text"
                value={q.label}
                onChange={(e) =>
                  updateField(q.id, {
                    label: e.target.value.slice(0, MAX_LABEL_LENGTH),
                  })
                }
                placeholder="e.g. How many leads do you wish to generate per month?"
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  fontSize: 12,
                  boxSizing: "border-box",
                  marginBottom: 8,
                }}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <label
                  style={{
                    fontSize: 11,
                    color: "#4b5563",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  Type
                  <select
                    value={q.type}
                    onChange={(e) =>
                      updateField(q.id, { type: e.target.value })
                    }
                    style={{
                      padding: "3px 6px",
                      border: "1px solid #d1d5db",
                      borderRadius: 4,
                      fontSize: 11,
                    }}
                  >
                    {INTAKE_QUESTION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>

                <label
                  style={{
                    fontSize: 11,
                    color: "#4b5563",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(q.required)}
                    onChange={(e) =>
                      updateField(q.id, { required: e.target.checked })
                    }
                  />
                  Required
                </label>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addQuestion}
            disabled={atCap}
            style={{
              marginTop: 6,
              padding: "8px 12px",
              border: "1px dashed #94a3b8",
              borderRadius: 8,
              background: "transparent",
              color: atCap ? "#9ca3af" : "#4a7feb",
              fontSize: 12,
              fontWeight: 500,
              cursor: atCap ? "not-allowed" : "pointer",
              width: "100%",
            }}
          >
            {atCap
              ? `Maximum reached — ${MAX_INTAKE_QUESTIONS} questions`
              : `+ Add question (${drafts.length}/${MAX_INTAKE_QUESTIONS})`}
          </button>

          {error && (
            <div
              style={{
                marginTop: 10,
                padding: "6px 10px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#b91c1c",
                borderRadius: 6,
                fontSize: 11,
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "6px 14px",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              background: "#fff",
              color: "#374151",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            style={{
              padding: "6px 14px",
              border: "none",
              borderRadius: 6,
              background: "#4a7feb",
              color: "#fff",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            Save questions
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Per-app department editor.
 *
 * Renders current chips (each removable) + a `+ Add` button. Clicking
 * `+ Add` opens an input. Below the input, a dropdown of `suggestions`
 * (existing tenant departments for this app, supplied by the parent)
 * lets the admin reuse a name in one click. Typing + Enter still
 * creates a brand-new department.
 *
 * Each chip now also carries a small gear button. Clicking it opens
 * the IntakeQuestionsPopover for that department.
 *
 * Fully controlled:
 *   `departments`         — string[]
 *   `intakeQuestions`     — { [dept]: Array<{id,label,type,required}> }
 *   `onChange(next)`      — fires on every add/remove of a dept
 *   `onIntakeChange(dept, nextQuestions)` — fires on Save in the popover
 */
function DepartmentsCell({
  departments,
  intakeQuestions = {},
  suggestions = [],
  disabled,
  onChange,
  onIntakeChange,
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const list = Array.isArray(departments) ? departments : [];

  const availableSuggestions = useMemo(() => {
    const attached = new Set(list);
    return (suggestions || []).filter(
      (s) => typeof s === "string" && s.trim() && !attached.has(s)
    );
  }, [suggestions, list]);

  useEffect(() => {
    if (isAdding && inputRef.current) inputRef.current.focus();
  }, [isAdding]);

  useEffect(() => {
    if (!showSuggestions) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showSuggestions]);

  const commitDraft = () => {
    const value = draft.trim();
    if (!value) {
      setIsAdding(false);
      setDraft("");
      return;
    }
    if (!list.includes(value)) {
      onChange([...list, value]);
    }
    setDraft("");
    setIsAdding(false);
    setShowSuggestions(false);
  };

  const pickSuggestion = (value) => {
    if (!list.includes(value)) {
      onChange([...list, value]);
    }
    setDraft("");
    setIsAdding(false);
    setShowSuggestions(false);
  };

  const cancelDraft = () => {
    setDraft("");
    setIsAdding(false);
    setShowSuggestions(false);
  };

  const removeDepartment = (dept) => {
    onChange(list.filter((d) => d !== dept));
    // Also drop the questions for this department, if any.
    if (
      onIntakeChange &&
      intakeQuestions &&
      Object.prototype.hasOwnProperty.call(intakeQuestions, dept)
    ) {
      onIntakeChange(dept, []);
    }
  };

  return (
    <div
      ref={wrapRef}
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        alignItems: "center",
        minWidth: 160,
        position: "relative",
      }}
    >
      {list.map((dept) => {
        const qCount = Array.isArray(intakeQuestions?.[dept])
          ? intakeQuestions[dept].length
          : 0;
        return (
          <span
            key={dept}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 4px 2px 8px",
              borderRadius: 999,
              background: "#eef2ff",
              color: "#1e40af",
              fontSize: 11,
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {dept}
            {qCount > 0 && (
              <span
                title={`${qCount} intake question${qCount === 1 ? "" : "s"}`}
                style={{
                  background: "#c7d2fe",
                  color: "#1e3a8a",
                  borderRadius: 999,
                  padding: "0 6px",
                  fontSize: 10,
                  lineHeight: "16px",
                }}
              >
                {qCount}q
              </span>
            )}
            {!disabled && (
              <button
                type="button"
                onClick={() => setEditingDept(dept)}
                title={`Edit intake questions for ${dept}`}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#1e40af",
                  cursor: "pointer",
                  padding: "0 2px",
                  fontSize: 11,
                  lineHeight: 1,
                }}
              >
                ⚙
              </button>
            )}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeDepartment(dept)}
                title={`Remove ${dept}`}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#1e40af",
                  cursor: "pointer",
                  padding: 0,
                  fontSize: 13,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            )}
          </span>
        );
      })}

      {!disabled && !isAdding && (
        <button
          type="button"
          onClick={() => {
            setIsAdding(true);
            setShowSuggestions(availableSuggestions.length > 0);
          }}
          title="Add department"
          style={{
            border: "1px dashed #94a3b8",
            background: "transparent",
            color: "#475569",
            cursor: "pointer",
            borderRadius: 999,
            padding: "2px 8px",
            fontSize: 11,
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          + Add
        </button>
      )}

      {!disabled && isAdding && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={() =>
              setShowSuggestions(availableSuggestions.length > 0)
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitDraft();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancelDraft();
              }
            }}
            placeholder={
              availableSuggestions.length > 0
                ? "Pick or type a new one"
                : "NEW_DEPARTMENT"
            }
            style={{
              width: 140,
              padding: "2px 6px",
              border: "1px solid #94a3b8",
              borderRadius: 6,
              fontSize: 11,
            }}
          />
          <button
            type="button"
            onClick={commitDraft}
            style={{
              border: "none",
              background: "#4a7feb",
              color: "#fff",
              cursor: "pointer",
              borderRadius: 4,
              padding: "2px 6px",
              fontSize: 11,
            }}
          >
            ✓
          </button>
          <button
            type="button"
            onClick={cancelDraft}
            style={{
              border: "none",
              background: "#e5e7eb",
              color: "#374151",
              cursor: "pointer",
              borderRadius: 4,
              padding: "2px 6px",
              fontSize: 11,
            }}
          >
            ×
          </button>

          {showSuggestions && availableSuggestions.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                marginTop: 4,
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                zIndex: 20,
                minWidth: 180,
                maxHeight: 180,
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  padding: "6px 10px",
                  fontSize: 10,
                  color: "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                Existing departments
              </div>
              {availableSuggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => pickSuggestion(s)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "6px 10px",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    fontSize: 12,
                    color: "#111827",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#f3f4f6";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </span>
      )}

      {disabled && list.length === 0 && (
        <span style={{ fontSize: 11, color: "#94a3b8" }}>—</span>
      )}

      {editingDept && (
        <IntakeQuestionsPopover
          appName={editingDept.appName || editingDept} // fallback (only dept string passed)
          departmentName={
            typeof editingDept === "string" ? editingDept : editingDept.name
          }
          questions={intakeQuestions?.[editingDept] || []}
          onSave={(next) => {
            if (onIntakeChange) {
              onIntakeChange(editingDept, next);
            }
            setEditingDept(null);
          }}
          onClose={() => setEditingDept(null)}
        />
      )}
    </div>
  );
}

function TenantInvite({ initialMode = "invite", tenant_name, appData }) {
  const gts = JSON.parse(localStorage.getItem("global_tenant_settings"));
  const [currentMode, setCurrentMode] = useState(initialMode);
  const [appList, setAppList] = useState([]);

  const [initialLoading, setInitialLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedUserEmail, setSelectedUserEmail] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [createPermissions, setCreatePermissions] = useState({
    create_apps: false,
    create_users: false
  });
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [showTooltip, setShowTooltip] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isAdminUser, setIsAdminUser] = useState(true);

  // Hierarchy view state
  const [expandedDepartments, setExpandedDepartments] = useState({});
  const [hierarchySearch, setHierarchySearch] = useState("");

  // { app_name: [ "HR_DEPARTMENT", "TECH_DEPARTMENT" ] }
  const [departmentsByApp, setDepartmentsByApp] = useState({});

  // { app_name: { dept_name: [ {id, label, type, required} ] } }
  const [intakeQuestionsByApp, setIntakeQuestionsByApp] = useState({});

  const formRef = useRef(null);

  const meData = useSelector((state) => state.auth.meData);

  const extractRegionFromUrl = (url) => {
    const match = url.match(/https:\/\/([^.]+)\.apps2\./);
    return match ? match[1] : "us";
  };

  useEffect(() => {
    if (meData) {
      setIsAdminUser(meData.type === "tenant_type");
    }
  }, [meData]);

  useEffect(() => {
    setEmail("");
    setFullName("");
    setSelectedUsers([]);
    setSelectedUserEmail("");
    setSelectedRoles([]);
    setDepartmentsByApp({});
    setIntakeQuestionsByApp({});
    if (permissions.length > 0) {
      const resetPermissions = permissions.map(() => ({
        appSelected: false,
        ...ALL_PERMISSIONS.reduce((acc, perm) => {
          acc[perm] = false;
          return acc;
        }, {})
      }));
      setPermissions(resetPermissions);
    }
    setAllSelected(false);
    setCreatePermissions({ create_apps: false, create_users: false });
    setErrorMessage("");
  }, [currentMode]);

  useEffect(() => {
    const fetchAppNames = async () => {
      try {
        setInitialLoading(true);
        const isInternalHost =
          window.location.hostname === "122.160.157.99" ||
          window.location.hostname === "dev.tezkit.com";

        const urls = isInternalHost
          ? [`https://us.apps2.${backend_api_base_url}/all_app_names?tenant_id=${tenant_name}`]
          : [
            `https://in.apps2.${backend_api_base_url}/all_app_names?tenant_id=${tenant_name}`,
            `https://us.apps2.${backend_api_base_url}/all_app_names?tenant_id=${tenant_name}`,
          ];

        const results = await Promise.allSettled(
          urls.map((url) => fetch(url).then((res) => res.text()))
        );

        let combinedApps = [];
        results.forEach((result, idx) => {
          if (result.status === "fulfilled") {
            const url = urls[idx];
            const region = extractRegionFromUrl(url);
            const apps = JSON.parse(result.value);
            combinedApps = [...combinedApps, ...apps.map((a) => ({ ...a, region }))];
          } else {
            console.error(`Failed to fetch apps for URL: ${urls[idx]}`);
          }
        });

        setAppList(combinedApps);
        setPermissions(
          combinedApps.map(() => ({
            appSelected: false,
            ...ALL_PERMISSIONS.reduce((acc, perm) => {
              acc[perm] = false;
              return acc;
            }, {})
          }))
        );
      } catch (error) {
        console.error("Error fetching app names:", error);
        setErrorMessage("Failed to load applications. Please try again.");
      } finally {
        setInitialLoading(false);
      }
    };

    if (tenant_name && isAdminUser) fetchAppNames();
  }, [tenant_name, isAdminUser]);

  const fetchSupportUsers = async () => {
    if (!(tenant_name && isAdminUser)) return;
    setUsersLoading(true);
    try {
      const response = await fetch(
        `https://auth2.${backend_api_base_url}/get_support_users_of_tenant?tenant_name=${tenant_name}`
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("User fetch error:", error);
      setErrorMessage("Failed to load users. Please try again.");
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchSupportUsers();
  }, [tenant_name, isAdminUser]);

  const { hierarchy, missingRolesCount } = useMemo(() => {
    const normalized = users.map((u) => normalizeUser(u));
    const grouped = groupUsersByDepartment(normalized);
    const missing = normalized.filter((u) => u.roles.length === 0).length;
    return { hierarchy: grouped, missingRolesCount: missing };
  }, [users]);

  const departmentNames = useMemo(() => Object.keys(hierarchy), [hierarchy]);

  const filteredHierarchy = useMemo(() => {
    const q = hierarchySearch.trim().toLowerCase();
    if (!q) return hierarchy;

    const result = {};
    Object.entries(hierarchy).forEach(([dept, deptUsers]) => {
      const deptMatches = dept.toLowerCase().includes(q);
      const matchingUsers = deptUsers.filter(
        (u) =>
          String(u.name).toLowerCase().includes(q) ||
          String(u.email).toLowerCase().includes(q)
      );
      if (deptMatches || matchingUsers.length > 0) {
        result[dept] = deptMatches ? deptUsers : matchingUsers;
      }
    });
    return result;
  }, [hierarchy, hierarchySearch]);

  const departmentCatalogByApp = useMemo(() => {
    const catalog = {};
    for (const u of users) {
      const map = u?.departments;
      if (!map || typeof map !== "object") continue;
      for (const [appName, depts] of Object.entries(map)) {
        if (!Array.isArray(depts)) continue;
        if (!catalog[appName]) catalog[appName] = new Set();
        for (const d of depts) {
          const s = String(d || "").trim();
          if (s) catalog[appName].add(s);
        }
      }
    }
    const out = {};
    for (const [appName, set] of Object.entries(catalog)) {
      out[appName] = [...set].sort((a, b) => a.localeCompare(b));
    }
    return out;
  }, [users]);

  const toggleDepartment = (dept) => {
    setExpandedDepartments((prev) => ({ ...prev, [dept]: !prev[dept] }));
  };

  const expandAllDepartments = () => {
    const allExpanded = departmentNames.reduce((acc, d) => {
      acc[d] = true;
      return acc;
    }, {});
    setExpandedDepartments(allExpanded);
  };

  const collapseAllDepartments = () => setExpandedDepartments({});

  const fetchUserPermissions = async (userEmail) => {
    if (!userEmail || !tenant_name || !isAdminUser) return;
    const token = localStorage.getItem("token");
    if (!token) {
      setErrorMessage("Access token not found. Please login again.");
      return;
    }

    try {
      const response = await fetch(
        `https://auth2.${backend_api_base_url}/support_user_permissions?tenant_name=${tenant_name}&user_email=${encodeURIComponent(userEmail)}`,
        { headers: { Authorization: token } }
      );
      if (!response.ok) throw new Error(`Failed to fetch permissions: ${response.status}`);
      const data = await response.json();

      if (data && data.length > 0) {
        const userData = data[0];

        const resetPermissions = appList.map(() => ({
          appSelected: false,
          ...ALL_PERMISSIONS.reduce((acc, perm) => {
            acc[perm] = false;
            return acc;
          }, {})
        }));

        if (userData.apps && userData.apps.length > 0) {
          userData.apps.forEach((appPermission) => {
            const appIndex = appList.findIndex(
              (app) =>
                app.app_name === appPermission.app_name &&
                app.region === appPermission.region
            );
            if (appIndex !== -1) {
              resetPermissions[appIndex].appSelected = true;
              if (appPermission.permissions.includes("read"))
                resetPermissions[appIndex].read = true;
              if (appPermission.permissions.includes("delete"))
                resetPermissions[appIndex].delete = true;
              if (appPermission.permissions.includes("manage"))
                resetPermissions[appIndex].manage = true;
            }
          });
        }

        if (userData.users && userData.users.length > 0) {
          userData.users.forEach((userPermission) => {
            const appIndex = appList.findIndex(
              (app) =>
                app.app_name === userPermission.app_name &&
                app.region === userPermission.region
            );
            if (appIndex !== -1) {
              resetPermissions[appIndex].appSelected = true;
              if (userPermission.permissions.includes("read"))
                resetPermissions[appIndex].read = true;
              if (userPermission.permissions.includes("delete"))
                resetPermissions[appIndex].delete = true;
            }
          });
        }

        if (userData.generic_permissions) {
          setCreatePermissions({
            create_apps: userData.generic_permissions.apps?.includes("write") || false,
            create_users: userData.generic_permissions.users?.includes("write") || false
          });
        }

        setSelectedRoles(userData.user_roles || []);
        setDepartmentsByApp(userData.departments || {});
        setIntakeQuestionsByApp(userData.department_intake_questions || {});
        setPermissions(resetPermissions);
        setAllSelected(resetPermissions.every((p) => p.appSelected));
      }
    } catch (error) {
      console.error("Error fetching user permissions:", error);
      setErrorMessage("Failed to load user permissions. Please try again.");
    }
  };

  const handleAppToggle = (index) => {
    if (!isAdminUser) return;
    const updated = [...permissions];
    const isSelected = updated[index].appSelected;
    updated[index] = {
      appSelected: !isSelected,
      ...ALL_PERMISSIONS.reduce((acc, perm) => {
        acc[perm] = !isSelected;
        return acc;
      }, {})
    };
    setPermissions(updated);
    setAllSelected(updated.every((p) => p.appSelected));
  };

  const handlePermissionChange = (index, type) => {
    if (!isAdminUser) return;
    const updated = [...permissions];
    updated[index][type] = !updated[index][type];
    updated[index].appSelected = ALL_PERMISSIONS.some((perm) => updated[index][perm]);
    setPermissions(updated);
    setAllSelected(updated.every((p) => p.appSelected));
  };

  const handleAllSelected = () => {
    if (!isAdminUser) return;
    const newValue = !allSelected;
    const updated = permissions.map(() => ({
      appSelected: newValue,
      ...ALL_PERMISSIONS.reduce((acc, perm) => {
        acc[perm] = newValue;
        return acc;
      }, {})
    }));
    setPermissions(updated);
    setAllSelected(newValue);
  };

  const selectUserByEmail = (selectedEmail) => {
    if (!isAdminUser || !selectedEmail) return;
    setSelectedUserEmail(selectedEmail);
    setSelectedUsers((prev) =>
      prev.includes(selectedEmail) ? prev : [...prev, selectedEmail]
    );
    fetchUserPermissions(selectedEmail);
  };

  const handleUserSelect = (e) => {
    selectUserByEmail(e.target.value);
    e.target.value = "";
  };

  const removeSelectedUser = (emailToRemove) => {
    if (!isAdminUser) return;
    setSelectedUsers((prev) => prev.filter((u) => u !== emailToRemove));
    if (selectedUserEmail === emailToRemove) {
      setSelectedUserEmail("");
      setPermissions(
        appList.map(() => ({
          appSelected: false,
          ...ALL_PERMISSIONS.reduce((acc, perm) => {
            acc[perm] = false;
            return acc;
          }, {})
        }))
      );
      setAllSelected(false);
      setCreatePermissions({ create_apps: false, create_users: false });
      setSelectedRoles([]);
      setDepartmentsByApp({});
      setIntakeQuestionsByApp({});
    }
  };

  const handleCreatePermissionChange = (permissionId, checked) => {
    if (!isAdminUser) return;
    setCreatePermissions((prev) => ({ ...prev, [permissionId]: checked }));
  };

  const handleRoleToggle = (role) => {
    if (!isAdminUser) return;
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleDepartmentsChange = (appName, nextList) => {
    if (!isAdminUser) return;
    setDepartmentsByApp((prev) => {
      const merged = { ...prev };
      const cleaned = (nextList || [])
        .map((d) => String(d || "").trim())
        .filter(Boolean);
      const dedup = [];
      const seen = new Set();
      for (const d of cleaned) {
        if (seen.has(d)) continue;
        seen.add(d);
        dedup.push(d);
      }
      if (dedup.length === 0) {
        delete merged[appName];
      } else {
        merged[appName] = dedup;
      }
      return merged;
    });

    // Prune intake questions for departments that no longer exist.
    setIntakeQuestionsByApp((prev) => {
      const perApp = prev[appName];
      if (!perApp) return prev;
      const allowed = new Set(
        (nextList || []).map((d) => String(d || "").trim()).filter(Boolean)
      );
      const pruned = {};
      for (const [dept, qs] of Object.entries(perApp)) {
        if (allowed.has(dept) && Array.isArray(qs) && qs.length > 0) {
          pruned[dept] = qs;
        }
      }
      const merged = { ...prev };
      if (Object.keys(pruned).length === 0) {
        delete merged[appName];
      } else {
        merged[appName] = pruned;
      }
      return merged;
    });
  };

  const handleIntakeChange = (appName, dept, nextQuestions) => {
    if (!isAdminUser) return;
    setIntakeQuestionsByApp((prev) => {
      const merged = { ...prev };
      const perApp = { ...(merged[appName] || {}) };

      if (!Array.isArray(nextQuestions) || nextQuestions.length === 0) {
        delete perApp[dept];
      } else {
        // Trim, cap at MAX_INTAKE_QUESTIONS, sanitize.
        const cleaned = nextQuestions
          .slice(0, MAX_INTAKE_QUESTIONS)
          .map((q) => ({
            id: q.id || generateQuestionId(),
            label: String(q.label || "").trim().slice(0, MAX_LABEL_LENGTH),
            type: INTAKE_QUESTION_TYPES.includes(q.type) ? q.type : "text",
            required: Boolean(q.required),
          }))
          .filter((q) => q.label);
        if (cleaned.length === 0) {
          delete perApp[dept];
        } else {
          perApp[dept] = cleaned;
        }
      }

      if (Object.keys(perApp).length === 0) {
        delete merged[appName];
      } else {
        merged[appName] = perApp;
      }
      return merged;
    });
  };

  const getSelectedAppsPayload = () =>
    appList
      .map((app, index) => {
        const perms = permissions[index];
        if (!perms?.appSelected) return null;
        const selectedPermissions = [];
        if (perms.read) selectedPermissions.push("read");
        if (perms.delete) selectedPermissions.push("delete");
        if (perms.manage) selectedPermissions.push("manage");
        return {
          app_name: app.app_name,
          region: app.region,
          permissions: selectedPermissions
        };
      })
      .filter(Boolean);

  const getGenericPermissionsPayload = () => {
    const genericPerms = { apps: [], users: ["write"] };
    if (createPermissions.create_apps) genericPerms.apps.push("write");
    return genericPerms;
  };

  const getUsersPayload = () =>
    appList
      .map((app, index) => {
        const perms = permissions[index];
        if (!perms?.appSelected) return null;
        return {
          app_name: app.app_name,
          region: app.region,
          permissions: ["read", "write", "delete"]
        };
      })
      .filter(Boolean);

  const getDepartmentsPayload = (selectedApps) => {
    const allowedAppNames = new Set(
      (selectedApps || []).map((a) => a.app_name)
    );
    const out = {};
    Object.entries(departmentsByApp).forEach(([appName, depts]) => {
      if (!allowedAppNames.has(appName)) return;
      const cleaned = (depts || [])
        .map((d) => String(d || "").trim())
        .filter(Boolean);
      if (cleaned.length > 0) out[appName] = cleaned;
    });
    return out;
  };

  /**
   * Build the {app: {dept: [q, ...]}} payload for the backend.
   *
   * Filtering rules mirror `getDepartmentsPayload`:
   *   • Drop apps not being granted on this submission.
   *   • Drop depts not in the granted app's dept list.
   *   • Drop the app entirely when it has no remaining depts with
   *     questions.
   */
  const getIntakeQuestionsPayload = (selectedApps, departmentsPayload) => {
    const allowedAppNames = new Set(
      (selectedApps || []).map((a) => a.app_name)
    );
    const out = {};
    Object.entries(intakeQuestionsByApp).forEach(([appName, perDept]) => {
      if (!allowedAppNames.has(appName)) return;
      if (!perAppHasContent(perDept)) return;

      const allowedDepts = new Set(departmentsPayload[appName] || []);
      const cleaned = {};
      Object.entries(perDept).forEach(([dept, qs]) => {
        if (!allowedDepts.has(dept)) return;
        if (!Array.isArray(qs) || qs.length === 0) return;
        const sanitized = qs
          .slice(0, MAX_INTAKE_QUESTIONS)
          .map((q) => ({
            id: String(q.id || "").trim(),
            label: String(q.label || "").trim(),
            type: INTAKE_QUESTION_TYPES.includes(q.type) ? q.type : "text",
            required: Boolean(q.required),
          }))
          .filter((q) => q.id && q.label);
        if (sanitized.length > 0) cleaned[dept] = sanitized;
      });
      if (Object.keys(cleaned).length > 0) out[appName] = cleaned;
    });
    return out;
  };

  const perAppHasContent = (perDept) =>
    perDept &&
    typeof perDept === "object" &&
    Object.values(perDept).some(
      (qs) => Array.isArray(qs) && qs.length > 0
    );

  const submitInviteUser = async () => {
    if (isSubmitting || !isAdminUser) return;
    const token = localStorage.getItem("token");
    if (!token) {
      setErrorMessage("Access token not found. Please login again.");
      return;
    }

    const selectedApps = getSelectedAppsPayload();
    const departmentsPayload = getDepartmentsPayload(selectedApps);
    const intakeQuestionsPayload = getIntakeQuestionsPayload(
      selectedApps,
      departmentsPayload
    );

    const payload = {
      tenant_name,
      user_email: email,
      full_name: fullName || email.split("@")[0],
      apps: selectedApps,
      users: getUsersPayload(),
      generic_permissions: getGenericPermissionsPayload(),
      user_roles: selectedRoles,
      departments: departmentsPayload,
      department_intake_questions: intakeQuestionsPayload,
    };

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      const response = await fetch(`https://auth2.${backend_api_base_url}/invite_support`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch {
        const text = await response.text();
        responseData = { message: text || "Unknown error" };
      }

      if (response.ok) {
        setEmail("");
        setFullName("");
        setSelectedRoles([]);
        setDepartmentsByApp({});
        setIntakeQuestionsByApp({});
        setPermissions(
          permissions.map(() => ({
            appSelected: false,
            ...ALL_PERMISSIONS.reduce((acc, perm) => {
              acc[perm] = false;
              return acc;
            }, {})
          }))
        );
        setAllSelected(false);
        setCreatePermissions({ create_apps: false, create_users: false });
        fetchSupportUsers();
        alert("Success! Invite sent.");
      } else {
        setErrorMessage(`Error: ${response.status} - ${responseData.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Invite error:", error);
      setErrorMessage("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitUpdatePermissions = async () => {
    if (isSubmitting || !isAdminUser) return;
    const token = localStorage.getItem("token");
    if (!token) {
      setErrorMessage("Access token not found. Please login again.");
      return;
    }

    const selectedApps = getSelectedAppsPayload();
    const genericPerms = getGenericPermissionsPayload();
    const usersPayload = getUsersPayload();
    const departmentsPayload = getDepartmentsPayload(selectedApps);
    const intakeQuestionsPayload = getIntakeQuestionsPayload(
      selectedApps,
      departmentsPayload
    );

    if (
      selectedApps.length === 0 &&
      genericPerms.apps.length === 0 &&
      genericPerms.users.length === 0 &&
      usersPayload.length === 0
    ) {
      setErrorMessage("Please select at least one permission.");
      return;
    }

    const payload = {
      user_emails: selectedUsers,
      apps: selectedApps,
      users: usersPayload,
      generic_permissions: genericPerms,
      user_roles: selectedRoles,
      departments: departmentsPayload,
      department_intake_questions: intakeQuestionsPayload,
    };

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      const response = await fetch(
        `https://auth2.${backend_api_base_url}/update_support_permissions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload)
        }
      );

      let responseData;
      try {
        responseData = await response.json();
      } catch {
        const text = await response.text();
        responseData = { message: text || "Unknown error" };
      }

      if (response.ok) {
        setSelectedUsers([]);
        setSelectedUserEmail("");
        setSelectedRoles([]);
        setDepartmentsByApp({});
        setIntakeQuestionsByApp({});
        setPermissions(
          permissions.map(() => ({
            appSelected: false,
            ...ALL_PERMISSIONS.reduce((acc, perm) => {
              acc[perm] = false;
              return acc;
            }, {})
          }))
        );
        setAllSelected(false);
        setCreatePermissions({ create_apps: false, create_users: false });
        fetchSupportUsers();
        alert("Permissions updated successfully.");
      } else {
        if (response.status === 400) {
          setErrorMessage(`Bad Request (400): ${responseData.message || "Invalid data sent to server."}`);
        } else if (response.status === 401) {
          setErrorMessage("Unauthorized (401): Please login again.");
        } else if (response.status === 403) {
          setErrorMessage("Forbidden (403): You don't have permission to perform this action.");
        } else if (response.status === 404) {
          setErrorMessage("Not Found (404): The requested resource was not found.");
        } else if (response.status >= 500) {
          setErrorMessage(`Server Error (${response.status}): Please try again later.`);
        } else {
          setErrorMessage(`Error ${response.status}: ${responseData.message || "Unknown error"}`);
        }
      }
    } catch (error) {
      console.error("Update error:", error);
      if (error.name === "TypeError" && error.message.includes("Failed to fetch")) {
        setErrorMessage("Network error: Could not connect to server.");
      } else {
        setErrorMessage(`Error: ${error.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!isAdminUser) return;
    if (currentMode === "invite") submitInviteUser();
    else submitUpdatePermissions();
  };

  const handleModeChange = (e) => {
    if (!isAdminUser) return;
    setCurrentMode(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  if (!isAdminUser) {
    return (
      <div className={styles.container}>
        <div className={styles.adminOnlyMessage}>
          <div className={styles.adminOnlyIcon}>🔒</div>
          <h2 className={styles.adminOnlyTitle}>Only Available for Admin</h2>
          <p className={styles.adminOnlyText}>
            This feature is restricted to administrators only. Please contact your system
            administrator if you need access to invite or manage support users.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} ref={formRef} onKeyDown={handleKeyDown}>
      {gts?.multi_admin?.enable === false && (
        <div className={styles.multiTenantNotice}>
          <div className={styles.noticeIcon}>⚠️</div>
          <div>
            <strong>Multi-tenant feature is disabled.</strong> You need to enable Multi-tenant
            in <em>Global Tenant Settings</em> first to invite support users.
          </div>
        </div>
      )}

      {errorMessage && (
        <div className={styles.errorMessage}>
          <div className={styles.errorIcon}>❌</div>
          <div>{errorMessage}</div>
          <button className={styles.closeErrorBtn} onClick={() => setErrorMessage("")}>×</button>
        </div>
      )}

      <div className={styles.headerSection}>
        <h2 className={styles.title}>
          {currentMode === "invite"
            ? "Invite support user & assign permissions"
            : "Manage permissions for existing support users"}
        </h2>

        <div className={styles.inviteSelectorsWrap}>
          <div className={styles.selectSelectorsWrap}>
            <div className={styles.modeSelector}>
              <label htmlFor="mode-select">Mode: </label>
              <select
                id="mode-select"
                value={currentMode}
                onChange={handleModeChange}
                className={styles.modeDropdown}
                disabled={isSubmitting}
              >
                <option value="invite">Invite User</option>
                <option value="manage">Manage Permissions</option>
              </select>
            </div>

            <div className={styles.wrapper}>
              {currentMode === "invite" ? (
                <>
                  <div className={styles.emailInputWrap}>
                    <label>User Email</label>
                    <input
                      type="email"
                      value={email}
                      placeholder="Enter user email"
                      onChange={(e) => setEmail(e.target.value)}
                      className={styles.emailInput}
                      disabled={gts?.multi_admin?.enable === false || isSubmitting}
                    />
                  </div>
                  <div className={styles.nameInputWrap}>
                    <label>Full Name (Optional)</label>
                    <input
                      type="text"
                      value={fullName}
                      placeholder="Enter full name"
                      onChange={(e) => setFullName(e.target.value)}
                      className={styles.emailInput}
                      disabled={gts?.multi_admin?.enable === false || isSubmitting}
                    />
                  </div>
                </>
              ) : (
                <div className={styles.emailInputWrap}>
                  <label>Select User</label>
                  <select
                    onChange={handleUserSelect}
                    className={styles.modeDropdown}
                    defaultValue=""
                    disabled={gts?.multi_admin?.enable === false || isSubmitting || usersLoading}
                  >
                    <option value="" disabled>Select user</option>
                    {users.map((user, idx) => (
                      <option key={idx} value={user.email || user.user_email}>
                        {user.email || user.user_email}
                      </option>
                    ))}
                  </select>
                  {usersLoading && <div className={styles.loadingText}>Loading users...</div>}
                  <div className={styles.userTabs}>
                    {selectedUsers.map((userEmail) => (
                      <div className={styles.userTab} key={userEmail}>
                        {userEmail}
                        <button
                          type="button"
                          onClick={() => removeSelectedUser(userEmail)}
                          className={styles.removeBtn}
                          disabled={isSubmitting}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {currentMode === "manage" && (
            <div className={styles.hierarchySection}>
              <div className={styles.hierarchyHeader}>
                <div>
                  <h3 className={styles.hierarchyTitle}>Support User Hierarchy</h3>
                  <p className={styles.hierarchySubtitle}>
                    {tenant_name} &nbsp;→&nbsp; {departmentNames.length} department
                    {departmentNames.length === 1 ? "" : "s"} &nbsp;→&nbsp; {users.length} user
                    {users.length === 1 ? "" : "s"}
                  </p>
                </div>
                <div className={styles.hierarchyControls}>
                  <input
                    type="search"
                    value={hierarchySearch}
                    onChange={(e) => setHierarchySearch(e.target.value)}
                    placeholder="Search departments or users..."
                    className={styles.hierarchySearch}
                    disabled={usersLoading}
                  />
                  <button
                    type="button"
                    className={styles.hierarchyBtn}
                    onClick={expandAllDepartments}
                    disabled={usersLoading || departmentNames.length === 0}
                  >
                    Expand all
                  </button>
                  <button
                    type="button"
                    className={styles.hierarchyBtn}
                    onClick={collapseAllDepartments}
                    disabled={usersLoading || departmentNames.length === 0}
                  >
                    Collapse all
                  </button>
                </div>
              </div>

              {!usersLoading && users.length > 0 && missingRolesCount > 0 && (
                <div
                  style={{
                    margin: "0 0 12px 0",
                    padding: "8px 12px",
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "#7c2d12"
                  }}
                >
                  <span style={{ marginRight: 6 }}>ℹ️</span>
                  {missingRolesCount === users.length ? (
                    <>
                      The list endpoint hasn't returned <code>user_roles</code> yet, so all users
                      are grouped under <strong>Unassigned</strong>. Once the API includes{" "}
                      <code>user_roles</code> (joined from <code>SupportAccess</code>), departments
                      will appear automatically.
                    </>
                  ) : (
                    <>
                      <strong>{missingRolesCount}</strong> user
                      {missingRolesCount === 1 ? " has" : "s have"} no roles assigned and appear
                      under <strong>Unassigned</strong>.
                    </>
                  )}
                </div>
              )}

              {usersLoading ? (
                <p className={styles.loadingText}>Loading support users...</p>
              ) : Object.keys(filteredHierarchy).length === 0 ? (
                <p className={styles.noDataText}>
                  {users.length === 0
                    ? "No support users found for this tenant."
                    : "No departments or users match your search."}
                </p>
              ) : (
                <div className={styles.hierarchyTree} role="tree">
                  <div className={styles.hierarchyParent} role="treeitem" aria-expanded="true">
                    <span className={styles.hierarchyParentIcon}>🏢</span>
                    <span className={styles.hierarchyParentName}>{tenant_name || "Tenant"}</span>
                    <span className={styles.hierarchyBadge}>
                      {Object.keys(filteredHierarchy).length} department
                      {Object.keys(filteredHierarchy).length === 1 ? "" : "s"}
                    </span>
                  </div>

                  {Object.entries(filteredHierarchy).map(([dept, deptUsers]) => {
                    const isExpanded = expandedDepartments[dept] !== false;
                    return (
                      <div
                        key={dept}
                        className={styles.hierarchyDepartment}
                        role="treeitem"
                        aria-expanded={isExpanded}
                      >
                        <button
                          type="button"
                          className={styles.hierarchyDepartmentHeader}
                          onClick={() => toggleDepartment(dept)}
                          disabled={isSubmitting}
                        >
                          <span className={styles.hierarchyCaret}>{isExpanded ? "▾" : "▸"}</span>
                          <span className={styles.hierarchyDepartmentIcon}>📁</span>
                          <span className={styles.hierarchyDepartmentName}>{dept}</span>
                          <span className={styles.hierarchyBadge}>
                            {deptUsers.length} user{deptUsers.length === 1 ? "" : "s"}
                          </span>
                        </button>

                        {isExpanded && (
                          <ul className={styles.hierarchyUserList} role="group">
                            {deptUsers.map((u) => {
                              const isSelected = selectedUsers.includes(u.email);
                              const isActive = selectedUserEmail === u.email;
                              return (
                                <li
                                  key={u.email}
                                  className={`${styles.hierarchyUserItem} ${isActive ? styles.hierarchyUserActive : ""
                                    }`}
                                  role="treeitem"
                                >
                                  <span className={styles.hierarchyUserIcon}>👤</span>
                                  <div className={styles.hierarchyUserInfo}>
                                    <span className={styles.hierarchyUserName}>{u.name}</span>
                                    <span className={styles.hierarchyUserEmail}>{u.email}</span>
                                  </div>
                                  {u.roles.length > 0 && (
                                    <span className={styles.hierarchyRoles}>
                                      {u.roles.join(", ")}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    className={styles.hierarchySelectBtn}
                                    onClick={() => selectUserByEmail(u.email)}
                                    disabled={
                                      gts?.multi_admin?.enable === false || isSubmitting
                                    }
                                  >
                                    {isSelected ? "Selected" : "Select"}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className={styles.rolesSection}>
            <div className={styles.rolesHeader}>
              <h3>Roles</h3>
              <div
                className={styles.infoIcon}
                onMouseEnter={() => setShowTooltip("roles")}
                onMouseLeave={() => setShowTooltip(null)}
              >
                ⓘ
                {showTooltip === "roles" && (
                  <div className={styles.tooltip}>
                    Assign one or more roles to the support user. Roles define high-level access
                    and are stored alongside granular permissions.
                    {selectedRoles.length === 0 && currentMode === "manage" && (
                      <div style={{ marginTop: "8px", color: "#ff9800" }}>
                        <strong>None</strong> – no roles assigned.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.rolesGrid}>
              {AVAILABLE_ROLES.map((role) => (
                <div key={role} className={styles.roleCheckbox}>
                  <input
                    type="checkbox"
                    id={`role_${role}`}
                    checked={selectedRoles.includes(role)}
                    onChange={() => handleRoleToggle(role)}
                    disabled={gts?.multi_admin?.enable === false || isSubmitting}
                  />
                  <label htmlFor={`role_${role}`}>{role}</label>
                </div>
              ))}
              {currentMode === "manage" && selectedRoles.length === 0 && (
                <div className={styles.noRolesHint}>None</div>
              )}
            </div>
          </div>

          <div className={styles.createPermissions}>
            <div className={styles.createPermissionsHeader}>
              <h3>Create Permissions</h3>
              <div
                className={styles.infoIcon}
                onMouseEnter={() => setShowTooltip("create")}
                onMouseLeave={() => setShowTooltip(null)}
              >
                ⓘ
                {showTooltip === "create" && (
                  <div className={styles.tooltip}>
                    These permissions allow creating new resources that don't exist yet. They are
                    separate from app-specific permissions below.
                    <br />
                    <br />
                    <strong>Note:</strong> Users will automatically get "write" permission in
                    generic_permissions.users by default.
                  </div>
                )}
              </div>
            </div>
            <div className={styles.createPermissionsGrid}>
              {CREATE_PERMISSIONS.map((perm) => (
                <div key={perm.id} className={styles.createPermissionItem}>
                  <input
                    type="checkbox"
                    id={perm.id}
                    checked={createPermissions[perm.id]}
                    onChange={(e) => handleCreatePermissionChange(perm.id, e.target.checked)}
                    disabled={gts?.multi_admin?.enable === false || isSubmitting}
                  />
                  <label htmlFor={perm.id}>
                    {perm.label}
                    <div
                      className={styles.permissionInfo}
                      onMouseEnter={() => setShowTooltip(perm.id)}
                      onMouseLeave={() => setShowTooltip(null)}
                    >
                      ⓘ
                      {showTooltip === perm.id && (
                        <div className={styles.tooltip}>{perm.description}</div>
                      )}
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.submitWrapper}>
            <button
              type="button"
              className={styles.submitBtn}
              onClick={handleSubmit}
              disabled={
                gts?.multi_admin?.enable === false ||
                (currentMode === "invite" && !/\S+@\S+\.\S+/.test(email)) ||
                (currentMode === "manage" && selectedUsers.length === 0) ||
                !permissions.some((p) => p.appSelected) ||
                isSubmitting ||
                initialLoading
              }
            >
              {isSubmitting
                ? "Processing..."
                : currentMode === "invite"
                  ? "Send Invite"
                  : "Update Permissions"}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.permissionNote}>
        <h3 className={styles.authServerTitle}>ℹ️ Permissions</h3>
        <div className={styles.authServerDescription}>
          <strong>Note:</strong> Select permissions for each existing app:{" "}
          <span className={styles.availablePermission}>Read</span> (view),{" "}
          <span className={styles.availablePermission}>Delete</span> (remove), and{" "}
          <span className={styles.availablePermission}>Manage</span> (administer).
          <br />
          <br />
          <strong>Behind the scenes:</strong> When an app is selected, users automatically get
          ["read", "write", "delete"] permissions in the 'users' array, and
          generic_permissions.users always includes ["write"].
          <br />
          <br />
          <strong>Departments &amp; intake questions:</strong> For each app, you can
          create departments and, optionally, attach up to{" "}
          {MAX_INTAKE_QUESTIONS} intake questions to any department. Visitors
          picking that department will answer them before being connected;
          their answers will appear as the first message in the live chat.
        </div>
      </div>

      <div className={styles.tableWrapper}>
        {initialLoading ? (
          <p className={styles.loadingText}>Loading apps...</p>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleAllSelected}
                      style={{ marginRight: "10px" }}
                      disabled={gts?.multi_admin?.enable === false || isSubmitting}
                    />
                    All Apps
                  </th>
                  {ALL_PERMISSIONS.map((perm) => (
                    <th className={styles.th} key={perm}>
                      {perm.charAt(0).toUpperCase() + perm.slice(1)}
                    </th>
                  ))}
                  <th className={styles.th}>Departments</th>
                </tr>
              </thead>
              <tbody>
                {appList.map((app, index) => {
                  const appDepts = departmentsByApp[app.app_name] || [];
                  const appIntake = intakeQuestionsByApp[app.app_name] || {};
                  const suggestionsForApp =
                    departmentCatalogByApp[app.app_name] || [];
                  return (
                    <tr key={index}>
                      <td className={styles.td}>
                        <input
                          type="checkbox"
                          checked={permissions[index]?.appSelected || false}
                          onChange={() => handleAppToggle(index)}
                          style={{ marginRight: "10px" }}
                          disabled={gts?.multi_admin?.enable === false || isSubmitting}
                        />
                        {app.app_name} ({app.region.toUpperCase()})
                      </td>
                      {ALL_PERMISSIONS.map((perm) => (
                        <td className={styles.td} key={perm}>
                          <div className={styles.checkboxWrapper}>
                            <input
                              type="checkbox"
                              checked={permissions[index]?.[perm] || false}
                              onChange={() => handlePermissionChange(index, perm)}
                              disabled={gts?.multi_admin?.enable === false || isSubmitting}
                            />
                          </div>
                        </td>
                      ))}
                      <td className={styles.td}>
                        <DepartmentsCell
                          departments={appDepts}
                          intakeQuestions={appIntake}
                          suggestions={suggestionsForApp}
                          disabled={
                            gts?.multi_admin?.enable === false ||
                            isSubmitting ||
                            !permissions[index]?.appSelected
                          }
                          onChange={(next) =>
                            handleDepartmentsChange(app.app_name, next)
                          }
                          onIntakeChange={(dept, nextQuestions) =>
                            handleIntakeChange(
                              app.app_name,
                              dept,
                              nextQuestions
                            )
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {appList.length === 0 && !initialLoading && (
              <p className={styles.noDataText}>No apps found for this tenant.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default TenantInvite;